// ============================================================================
// OverviewTab — onglet VUE D'ENSEMBLE de l'ecran Admin (AD-B)
// ============================================================================
// Porte du PoC components/admin/overview-section.tsx aux standards du parc :
// les Kpi/QuickAction locales shadcn deviennent des KpiCard @gs/ui (releve Fable
// « REUSE, pas d'extraction »), les compteurs viennent du bundle serveur
// GET /api/admin/overview (assemble sur listTeamsSummary + listOrgPeople).
// DIVERGENCES ASSUMEES vs PoC :
//   - AUCUN KPI « Derniers envois » ni bouton de passe de rappels (P-ADM-7 /
//     STOP-11 : mailer ACS differe AD-G) — remplace par le KPI Objectifs ;
//   - pas de cartes « Actions rapides » : les onglets cibles (members/questions)
//     sont encore des placeholders AD-C/AD-F (doctrine POC-DEAD-LINK — aucun
//     raccourci vers un cadre vide ; la nav laterale suffit).
// Lot 3 (2026-07-26) : hints rapatries DANS la carte (AdminOverviewKpi) ;
// navigation clavier vers Membres / Equipes ; Comites + Objectifs statiques.

import { Building2, MessageSquare, Target, Users } from 'lucide-react';
import { SkeletonCard, QueryError } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { AdminOverviewResponse } from '../../hooks/useAdminTeams';
import { SectionHeader } from './SectionHeader';
import { AdminOverviewKpi } from './AdminOverviewKpi';

export function OverviewTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminOverviewResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.overview.loadError')} onRetry={onRetry} />;
  }

  const { members, teams, objectives } = data.kpis;

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.overview.title')}
        description={t('gest:admin.overview.description')}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <AdminOverviewKpi
          icon={Users}
          tone="brand"
          label={t('gest:admin.overview.kpis.members')}
          value={members.total}
          hint={
            <span className="text-slate-500 dark:text-slate-400">
              {t('gest:admin.overview.kpis.membersHint', {
                admins: members.admins,
                managers: members.managers,
                directors: members.directors,
                employees: members.employees,
              })}
            </span>
          }
          to="/admin?tab=members"
          activateLabel={t('gest:admin.overview.kpis.navigateMembers')}
        />

        <AdminOverviewKpi
          icon={Building2}
          tone="blue"
          label={t('gest:admin.overview.kpis.teams')}
          value={teams.total}
          hint={
            <span
              className={
                teams.withoutDirector > 0
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-emerald-600 dark:text-emerald-400'
              }
            >
              {teams.withoutDirector > 0
                ? t('gest:admin.overview.kpis.teamsWithoutDirector', { n: teams.withoutDirector })
                : t('gest:admin.overview.kpis.teamsAllDirected')}
            </span>
          }
          to="/admin?tab=teams"
          activateLabel={t('gest:admin.overview.kpis.navigateTeams')}
        />

        <AdminOverviewKpi
          icon={MessageSquare}
          tone="green"
          label={t('gest:admin.overview.kpis.committees')}
          value={members.committees.total}
          hint={
            <span className="text-slate-500 dark:text-slate-400">
              {t('gest:admin.overview.kpis.committeesHint', {
                ce: members.committees.ce,
                cg: members.committees.cg,
              })}
            </span>
          }
        />

        <AdminOverviewKpi
          icon={Target}
          tone="brand"
          label={t('gest:admin.overview.kpis.objectives')}
          value={`${objectives.done}/${objectives.total}`}
          hint={
            <span className="text-slate-500 dark:text-slate-400">
              {t('gest:admin.overview.kpis.objectivesHint', { n: objectives.inProgress })}
            </span>
          }
        />
      </div>
    </div>
  );
}
